import type { FC, ReactNode } from 'react'
import type { Product } from '@/types'

import Image from 'next/image'
import { ArrowRight, Plus } from 'lucide-react'
import { formatPrice, productCmsKey } from '@/lib/format'
import { useGlassSheen } from '@/hooks/use-glass-sheen'
import { useBag } from '@/components/generals/bag-provider'
import { ProductImage } from '@/components/generals/product-image'

const HERO_FACTS = [
    { label: "Founded", value: "1908" },
    { label: "Ateliers", value: "Paris" },
    { label: "Care", value: "For life" }
]

interface HeroSectionProps {
    product: Product | null
    onView: (product: Product) => void
}

export const HeroSection: FC<HeroSectionProps> = ({ product, onView }): ReactNode => {
    const { addItem } = useBag()
    const card = useGlassSheen()

    return (
        <section
            id='hero'
            aria-labelledby='hero-title'
            className='relative isolate overflow-hidden pb-20 pt-32 sm:pt-40 lg:flex lg:min-h-[100svh] lg:items-center lg:pb-24 lg:pt-36'
        >
            <div aria-hidden='true' className='absolute inset-0 -z-10 overflow-hidden'>
                {product?.image && (
                    <ProductImage
                        src={product.image}
                        alt=''
                        sizes='25vw'
                        className='scale-125 opacity-50 blur-3xl saturate-150'
                    />
                )}

                <div className='absolute inset-0 bg-linear-to-b from-ivory/30 via-ivory/60 to-ivory' />
                <div className='ambient-orb -left-40 top-24 size-[34rem] bg-champagne/70' />
                <div className='ambient-orb -right-24 bottom-0 size-[28rem] bg-gold/30 [animation-delay:-8s]' />
            </div>

            <div className='mx-auto grid w-full max-w-7xl items-center gap-16 px-5 sm:px-8 lg:grid-cols-[1.08fr_0.92fr] lg:gap-12'>
                <div>
                    <p className='glass glass-clear inline-flex items-center gap-2.5 rounded-full px-4 py-2 text-[0.66rem] uppercase tracking-[0.28em] text-espresso/80 animate-in fade-in slide-in-from-bottom-2 duration-700'>
                        <span aria-hidden='true' className='size-1.5 rounded-full bg-gold-deep' />
                        Autumn–Winter 2026 collection
                    </p>

                    <h1
                        id='hero-title'
                        className='mt-8 font-heading text-[clamp(3.3rem,8.4vw,7.6rem)] font-light leading-[0.92] tracking-[-0.025em] text-espresso animate-in fade-in slide-in-from-bottom-4 fill-mode-both delay-150 duration-1000'
                    >
                        Made slowly.
                        <br />
                        Kept for <em className='text-gilded font-normal'>generations.</em>
                    </h1>

                    <p className='mt-8 max-w-xl text-lg leading-relaxed text-muted-foreground animate-in fade-in slide-in-from-bottom-4 fill-mode-both delay-300 duration-1000'>
                        Handbags, fine jewelry and gloves, each made from first cut to final polish by a single artisan in our Paris ateliers. Released in numbered editions, and offered first to the members of Le Cercle.
                    </p>

                    <div className='mt-10 flex flex-wrap items-center gap-3 animate-in fade-in slide-in-from-bottom-4 fill-mode-both delay-500 duration-1000'>
                        <a
                            href='#collection'
                            className='group inline-flex h-14 items-center gap-3 rounded-full bg-espresso pl-7 pr-2 text-[0.76rem] uppercase tracking-[0.18em] text-ivory shadow-[0_18px_40px_-18px_rgba(34,28,22,0.7)] transition-colors duration-300 hover:bg-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-deep'
                        >
                            Explore the collection
                            <span className='flex size-10 items-center justify-center rounded-full bg-ivory/10 transition-transform duration-500 group-hover:translate-x-1'>
                                <ArrowRight className='size-4' strokeWidth={1.5} />
                            </span>
                        </a>

                        <a
                            href='#cercle'
                            className='glass inline-flex h-14 items-center rounded-full px-7 text-[0.76rem] uppercase tracking-[0.18em] text-espresso transition-transform duration-500 hover:-translate-y-0.5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-deep'
                        >
                            Join Le Cercle
                        </a>
                    </div>

                    <dl className='mt-14 grid max-w-lg grid-cols-3 gap-6 border-t border-espresso/10 pt-8 animate-in fade-in fill-mode-both delay-700 duration-1000'>
                        {HERO_FACTS.map((fact) => (
                            <div key={fact.label}>
                                <dt className='text-[0.66rem] uppercase tracking-[0.24em] text-muted-foreground'>
                                    {fact.label}
                                </dt>

                                <dd className='mt-2 font-heading text-2xl text-espresso sm:text-3xl'>
                                    {fact.value}
                                </dd>
                            </div>
                        ))}
                    </dl>
                </div>

                <div className='relative mx-auto w-full max-w-[32rem] lg:max-w-none'>
                    {product ? (
                        <>
                            <div className='relative aspect-[4/5] overflow-hidden rounded-[2.75rem] bg-sand shadow-[0_40px_80px_-40px_rgba(34,28,22,0.55)] ring-1 ring-white/60 animate-in fade-in zoom-in-95 fill-mode-both duration-1000'>
                                <ProductImage
                                    src={product.image}
                                    alt={product.image_alt ?? product.name}
                                    sizes='(min-width: 1024px) 42vw, 90vw'
                                    preload
                                    className='motion-safe:animate-ken-burns'
                                />

                                <div className='pointer-events-none absolute inset-0 bg-linear-to-t from-espresso/25 via-transparent to-white/10' />
                            </div>

                            {product.edition && (
                                <p className='glass glass-clear absolute right-4 top-4 rounded-full px-4 py-2 text-[0.64rem] uppercase tracking-[0.22em] text-espresso sm:-right-5 sm:top-10 motion-safe:animate-float'>
                                    <span data-mdlfy-cms={productCmsKey(product, 'edition')}>
                                        {product.edition}
                                    </span>
                                </p>
                            )}

                            <div
                                ref={card.ref}
                                onPointerMove={card.onPointerMove}
                                onPointerLeave={card.onPointerLeave}
                                className='glass absolute inset-x-3 bottom-3 rounded-[2rem] p-5 text-espresso animate-in fade-in slide-in-from-bottom-6 fill-mode-both delay-700 duration-1000 sm:inset-x-auto sm:-left-10 sm:bottom-10 sm:w-[23rem] sm:p-6'
                            >
                                <p className='text-[0.62rem] uppercase tracking-[0.26em] text-espresso/60'>
                                    The piece of the season
                                </p>

                                <div className='mt-2 flex items-end justify-between gap-4'>
                                    <div className='min-w-0'>
                                        <p
                                            data-mdlfy-cms={productCmsKey(product, 'name')}
                                            className='font-heading text-[2rem] leading-none'
                                        >
                                            {product.name}
                                        </p>

                                        {product.material && (
                                            <p
                                                data-mdlfy-cms={productCmsKey(product, 'material')}
                                                className='mt-2 truncate text-xs text-espresso/70'
                                            >
                                                {product.material}
                                            </p>
                                        )}
                                    </div>

                                    <p className='shrink-0 font-heading text-2xl'>
                                        {formatPrice(product.price)}
                                    </p>
                                </div>

                                <div className='mt-5 flex gap-2'>
                                    <button
                                        type='button'
                                        onClick={() => addItem(product)}
                                        className='flex h-11 flex-1 items-center justify-center gap-2 rounded-full bg-espresso text-[0.7rem] uppercase tracking-[0.18em] text-ivory transition-colors hover:bg-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-deep'
                                    >
                                        <Plus className='size-4' strokeWidth={1.5} />
                                        Add to bag
                                    </button>

                                    <button
                                        type='button'
                                        onClick={() => onView(product)}
                                        className='h-11 rounded-full bg-white/60 px-5 text-[0.7rem] uppercase tracking-[0.18em] ring-1 ring-espresso/10 transition-colors hover:bg-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-deep'
                                    >
                                        Details
                                    </button>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className='relative aspect-[4/5] overflow-hidden rounded-[2.75rem] bg-sand shadow-[0_40px_80px_-40px_rgba(34,28,22,0.55)]'>
                            <Image
                                src='/images/story/workshop.jpg'
                                alt="The Aveline atelier bench in soft morning light"
                                fill
                                preload
                                sizes='(min-width: 1024px) 42vw, 90vw'
                                className='object-cover'
                            />

                            <div className='glass absolute inset-x-4 bottom-4 rounded-[2rem] p-6 text-espresso'>
                                <p className='text-[0.62rem] uppercase tracking-[0.26em] text-espresso/60'>
                                    The new collection
                                </p>

                                <p className='mt-2 font-heading text-3xl leading-tight'>
                                    Being finished in our ateliers
                                </p>

                                <a
                                    href='#cercle'
                                    className='mt-4 inline-flex text-[0.7rem] uppercase tracking-[0.2em] text-gold-deep underline underline-offset-4'
                                >
                                    Be the first to know
                                </a>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <a
                href='#collection'
                aria-label="Scroll to the collection"
                className='absolute bottom-6 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-3 text-[0.6rem] uppercase tracking-[0.32em] text-espresso/50 transition-colors hover:text-espresso lg:flex'
            >
                Scroll
                <span aria-hidden='true' className='h-10 w-px bg-linear-to-b from-espresso/40 to-transparent' />
            </a>
        </section>
    )
}
